import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * Shared messages-unread-count store.
 *
 * Sister context to NotificationsContext but counts THREADS with unread
 * direct messages (not engagement notifications). Same polling rules:
 * 30s background, plus refresh on route change so opening a thread
 * immediately clears the sidebar badge.
 */
const MessagesContext = createContext({ unreadCount: 0, refresh: async () => {} });

export function MessagesProvider({ children }) {
    const { user } = useAuth();
    const location = useLocation();
    const [unreadCount, setUnreadCount] = useState(0);

    const refresh = useCallback(async () => {
        if (!user) {
            setUnreadCount(0);
            return;
        }
        try {
            const { data } = await api.get("/messages/unread-count");
            setUnreadCount(data?.count || 0);
        } catch (_e) {
            // Network blip — keep last known value.
        }
    }, [user]);

    useEffect(() => {
        refresh();
        if (!user) return undefined;
        const t = setInterval(refresh, 30_000);
        return () => clearInterval(t);
    }, [refresh, user]);

    useEffect(() => { refresh(); }, [location.pathname, refresh]);

    return (
        <MessagesContext.Provider value={{ unreadCount, refresh }}>
            {children}
        </MessagesContext.Provider>
    );
}

export function useMessages() {
    return useContext(MessagesContext);
}
